import type { CSSProperties } from 'react';

import { ContainerElement } from '@/containerElement';
import { Heading1Element, Heading4Element, Heading6Element } from '@/headingElement';
import { ImageElement } from '@/imageElement';
import { ParagraphElement } from '@/paragraphElement';
import { ApplePodcastsLogo, SpotifyLogo, YouTubeLogo } from '@/podcastPlatformLogos';
import { SerializeTextLeaf } from '@/textLeaf';
import { cn } from '@/utils';

import { RSS_FEED_EPISODE_DEFAULTS } from './constants';
import { ListenOnButton } from './ListenOnButton';
import { PodcastAudioPlayer } from './PodcastAudioPlayer';
import { rssFeedModalContentContainer } from './RSSFeed.css';
import type { RSSFeedEpisodeProps } from './RSSFeed.types';
import { formatDate, getEpisodeUrl, normalizeToArray } from './util';

export const PodcastRssFeedEpisode = ({
  theme,
  rssFeedData,
  url,
  rssFeedMap,
  podcastEpisode,
  applePodcastEpisode,
  spotifyPodcastEpisode,
  youtubePodcastEpisode,
}: RSSFeedEpisodeProps) => {
  if (!podcastEpisode) {
    return null;
  }
  const feedItems = normalizeToArray(rssFeedData?.rss?.channel?.item);
  const shownData = feedItems.slice(0, 10).filter((item) => item.guid !== podcastEpisode.guid);
  const applePodcastURL = applePodcastEpisode?.trackViewUrl;
  const spotifyPodcastURL = spotifyPodcastEpisode?.external_urls?.spotify;
  const youtubePodcastURL = youtubePodcastEpisode?.link?.['@href'];
  const showListenOn = applePodcastURL || spotifyPodcastURL || youtubePodcastURL;
  const episodeImageURL =
    podcastEpisode?.['itunes:image']?.['@href'] ||
    rssFeedData?.rss?.channel?.image?.url ||
    rssFeedData?.rss?.channel?.['itunes:image']?.['@href'] ||
    applePodcastEpisode?.artworkUrl600 ||
    '';

  const shouldShowYouTubeEmbed = !!youtubePodcastURL;

  const listenOn = (className?: string) =>
    showListenOn ? (
      <div className={cn('flex-1 flex flex-col gap-5', className)}>
        <Heading4Element
          element={{
            type: 'heading',
            attrs: {
              textAlign: 'left',
              padding: '0px',
              mobilePadding: '0px',
              width: 'fit-content',
              level: 4,
              anchorId: 'heading4',
              anchorTitle: 'heading4',
              anchorIdSync: true,
              anchorEnabled: true,
              anchorTitleSync: true,
              customAttributes: {},
              id: 'heading4',
            },
            content: [],
          }}
          className="text-sm"
        >
          <SerializeTextLeaf
            leaf={{
              text: 'Listen on',
              marks: [
                {
                  type: 'textStyle',
                  attrs: {
                    fontSize: 18,
                    fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.titleFontFamily,
                    color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.titleTextColor,
                    fontWeight: RSS_FEED_EPISODE_DEFAULTS.titleFontWeight,
                  },
                },
              ],
            }}
          />
        </Heading4Element>

        <div className={cn('flex-1 flex lg:flex-col gap-5 items-center lg:items-start flex-wrap')}>
          {applePodcastURL && (
            <ListenOnButton url={applePodcastURL} Icon={ApplePodcastsLogo} label="Apple Podcasts" theme={theme} />
          )}
          {spotifyPodcastURL && (
            <ListenOnButton url={spotifyPodcastURL} Icon={SpotifyLogo} label="Spotify" theme={theme} />
          )}
          {youtubePodcastURL && (
            <ListenOnButton
              url={youtubePodcastURL}
              Icon={YouTubeLogo}
              label="YouTube"
              theme={theme}
              iconClassName="h-5 w-auto shrink-0"
            />
          )}
        </div>
      </div>
    ) : null;

  const audioPlayer = (className?: string) => (
    <PodcastAudioPlayer
      className={className}
      podcastEpisode={podcastEpisode}
      applePodcastEpisode={applePodcastEpisode}
      theme={theme}
    />
  );

  const youtubeEmbed = (className?: string) => (
    <div className={cn('relative w-full min-w-[200px] min-h-[200px]', className)} style={{ aspectRatio: '16 / 9' }}>
      <iframe
        className="absolute top-0 left-0 w-full h-full"
        src={`https://www.youtube.com/embed/${youtubePodcastEpisode?.['yt:videoId']}`}
        title="YouTube video player"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );

  return (
    <div
      className={cn(rssFeedModalContentContainer, 'flex flex-col h-auto p-4 lg:p-12 gap-8 lg:gap-16')}
      style={
        {
          '--width': RSS_FEED_EPISODE_DEFAULTS.pageWidth,
          '--max-width': RSS_FEED_EPISODE_DEFAULTS.pageWidthMax,
          backgroundColor: theme.background_color || RSS_FEED_EPISODE_DEFAULTS.pageBackgroundColor,
        } as CSSProperties
      }
    >
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'container',
            padding: '0px',
            backgroundColor: 'transparent',
            gap: '32px',
            flexDirection: 'row',
            horizontalAlign: 'left',
            mobileFlexDirection: 'column',
            mobileHorizontalAlign: 'center',
          },
          content: [],
        }}
      >
        {!shouldShowYouTubeEmbed && (
          <ImageElement
            element={{
              type: 'image',
              attrs: {
                src: episodeImageURL,
                width: 250,
                height: 'auto',
                aspectRatio: 1,
                objectFit: 'cover',
                borderRadius: RSS_FEED_EPISODE_DEFAULTS.imageBorderRadius,
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.10), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                borderColor: 'rgba(128, 128, 128, 0.15)',
                borderWidth: 1,
                borderStyle: 'solid',
              },
            }}
          />
        )}
        <ContainerElement
          element={{
            type: 'container',
            attrs: {
              id: 'container',
              padding: '0px',
              backgroundColor: 'transparent',
              gap: '16px',
              flexDirection: 'column',
              horizontalAlign: 'left',
            },
            content: [],
          }}
        >
          <ParagraphElement
            element={{
              type: 'paragraph',
              attrs: {
                textAlign: 'left',
                padding: '0px',
              },
            }}
            className="line-clamp-2 text-ellipsis opacity-60"
          >
            <SerializeTextLeaf
              leaf={{
                text: formatDate(podcastEpisode.pubDate),
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: RSS_FEED_EPISODE_DEFAULTS.dateFontSize,
                      fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.dateFontFamily,
                      color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.dateTextColor,
                      fontWeight: RSS_FEED_EPISODE_DEFAULTS.dateFontWeight,
                    },
                  },
                ],
              }}
            />
          </ParagraphElement>
          <Heading1Element
            element={{
              type: 'heading',
              attrs: {
                id: 'heading1',
                anchorId: 'heading1',
                padding: '0px',
                anchorTitle: 'heading1',
                anchorIdSync: true,
                anchorEnabled: true,
                anchorTitleSync: true,
                customAttributes: {},
                textAlign: 'left',
                level: 1,
              },
              content: [
                {
                  text: podcastEpisode.title,
                },
              ],
            }}
          >
            <SerializeTextLeaf
              leaf={{
                text: podcastEpisode.title,
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: RSS_FEED_EPISODE_DEFAULTS.titleFontSize,
                      fontFamily: theme.header_font || RSS_FEED_EPISODE_DEFAULTS.titleFontFamily,
                      color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.titleTextColor,
                      fontWeight: RSS_FEED_EPISODE_DEFAULTS.titleFontWeight,
                    },
                  },
                ],
              }}
            />
          </Heading1Element>
          {applePodcastEpisode?.shortDescription && (
            <ParagraphElement
              element={{
                type: 'paragraph',
                attrs: {
                  textAlign: 'left',
                  padding: '0px',
                },
              }}
              className="line-clamp-2 text-ellipsis"
            >
              <SerializeTextLeaf
                leaf={{
                  text: applePodcastEpisode.shortDescription,
                  marks: [
                    {
                      type: 'textStyle',
                      attrs: {
                        fontSize: RSS_FEED_EPISODE_DEFAULTS.descriptionFontSize,
                        fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.descriptionFontFamily,
                        color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.descriptionTextColor,
                        fontWeight: RSS_FEED_EPISODE_DEFAULTS.descriptionFontWeight,
                      },
                    },
                  ],
                }}
              />
            </ParagraphElement>
          )}
        </ContainerElement>
      </ContainerElement>

      {/* YouTube Player */}
      {shouldShowYouTubeEmbed && youtubeEmbed('lg:hidden')}
      {podcastEpisode.enclosure && audioPlayer('lg:hidden')}

      {listenOn('lg:hidden')}
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'container',
            padding: '0px',
            backgroundColor: 'transparent',
            gap: '32px',
            flexDirection: 'row',
            horizontalAlign: 'left',
            mobileFlexDirection: 'column',
          },
          content: [],
        }}
      >
        <ContainerElement
          element={{
            type: 'container',
            attrs: {
              id: 'container',
              padding: '0px',
              backgroundColor: 'transparent',
              gap: '32px',
              flexDirection: 'column',
              width: 700,
            },
            content: [],
          }}
          className="w-full lg:w-[700px]"
        >
          {/* MP3 Player */}
          {shouldShowYouTubeEmbed && youtubeEmbed('hidden lg:flex')}
          {podcastEpisode.enclosure && audioPlayer('hidden lg:flex')}

          <ParagraphElement
            element={{
              type: 'paragraph',
              attrs: {
                textAlign: 'left',
                padding: '0px',
              },
            }}
          >
            <div
              className="whitespace-pre-line [&_a]:underline"
              style={{
                fontSize: RSS_FEED_EPISODE_DEFAULTS.contentFontSize,
                fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.contentFontFamily,
                color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.contentTextColor,
                fontWeight: RSS_FEED_EPISODE_DEFAULTS.contentFontWeight,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: podcastEpisode?.['content:encoded'] || podcastEpisode.description }}
            />
          </ParagraphElement>
        </ContainerElement>

        <div
          className="flex-1 h-full min-h-0 flex flex-col gap-16 lg:border-l lg:pl-8"
          style={{
            borderColor: 'rgba(128, 128, 128, 0.15)',
          }}
        >
          {listenOn('hidden lg:flex')}

          <div className="flex-1 flex flex-col gap-5">
            <Heading4Element
              element={{
                type: 'heading',
                attrs: {
                  textAlign: 'left',
                  padding: '0px',
                  level: 4,
                  mobilePadding: '0px',
                  anchorId: 'heading4',
                  anchorTitle: 'heading4',
                  anchorIdSync: true,
                  anchorEnabled: true,
                  anchorTitleSync: true,
                  customAttributes: {},
                  id: 'heading4',
                },
                content: [],
              }}
              className="line-clamp-2 text-ellipsis text-sm"
            >
              <SerializeTextLeaf
                leaf={{
                  text: 'Recent episodes',
                  marks: [
                    {
                      type: 'textStyle',
                      attrs: {
                        fontSize: 18,
                        fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.titleFontFamily,
                        color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.titleTextColor,
                        fontWeight: RSS_FEED_EPISODE_DEFAULTS.titleFontWeight,
                      },
                    },
                  ],
                }}
              />
            </Heading4Element>

            {shownData?.map((entry) => (
              <ContainerElement
                href={entry.link}
                key={entry.guid}
                className={cn('flex items-center cursor-pointer')}
                target="_blank"
                rel="noopener noreferrer"
                element={{
                  type: 'container',
                  attrs: {
                    id: 'podcast-container',
                    padding: 0,
                    position: 'relative',
                    backgroundColor: 'transparent',
                    height: 'auto',
                    overflow: 'hidden',
                    gap: '8px',
                    flexDirection: 'row',
                    horizontalAlign: 'center',
                  },
                  content: [],
                }}
                onClick={() => {
                  const rssFeedItem = rssFeedMap?.[url || ''];
                  const rssFeedUUID = rssFeedItem?.uuid;
                  const episodeURL = getEpisodeUrl(entry.title || '', rssFeedUUID || '');
                  window.open(episodeURL, '_blank');
                }}
              >
                <ImageElement
                  element={{
                    type: 'image',
                    attrs: {
                      src:
                        entry['itunes:image']?.['@href'] ||
                        rssFeedData?.rss?.channel?.image?.url ||
                        rssFeedData?.rss?.channel?.['itunes:image']?.['@href'],
                      width: 64,
                      height: 64,
                      aspectRatio: 1,
                      objectFit: 'cover',
                      borderRadius: '8px',
                      borderColor: 'rgba(128, 128, 128, 0.15)',
                      borderWidth: 1,
                      borderStyle: 'solid',
                    },
                  }}
                />
                <ContainerElement
                  element={{
                    type: 'container',
                    attrs: {
                      id: 'container',
                      backgroundColor: 'transparent',
                      padding: '0px',
                      gap: '4px',
                    },
                    content: [],
                  }}
                >
                  <Heading6Element
                    element={{
                      type: 'heading',
                      attrs: {
                        id: 'heading6',
                        anchorId: 'heading6',
                        padding: '0px',
                        anchorTitle: 'heading6',
                        anchorIdSync: true,
                        anchorEnabled: true,
                        anchorTitleSync: true,
                        customAttributes: {},
                        textAlign: 'left',
                        level: 3,
                        lineHeight: '16px',
                      },
                      content: [
                        {
                          text: entry.title,
                        },
                      ],
                    }}
                    className="line-clamp-2 text-ellipsis"
                  >
                    <SerializeTextLeaf
                      leaf={{
                        text: entry.title,
                        marks: [
                          {
                            type: 'textStyle',
                            attrs: {
                              fontSize: 14,
                              fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.titleFontFamily,
                              color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.titleTextColor,
                              fontWeight: RSS_FEED_EPISODE_DEFAULTS.titleFontWeight,
                              lineHeight: '16px',
                            },
                          },
                        ],
                      }}
                    />
                  </Heading6Element>
                  <ParagraphElement
                    element={{
                      type: 'paragraph',
                      attrs: {
                        textAlign: 'left',
                        padding: '0px',
                        lineHeight: '16px',
                      },
                    }}
                    className="opacity-60"
                  >
                    <SerializeTextLeaf
                      leaf={{
                        text: formatDate(entry.pubDate),
                        marks: [
                          {
                            type: 'textStyle',
                            attrs: {
                              fontSize: 12,
                              lineHeight: '16px',
                              fontFamily: theme.body_font || RSS_FEED_EPISODE_DEFAULTS.dateFontFamily,
                              color: theme.text_on_background_color || RSS_FEED_EPISODE_DEFAULTS.dateTextColor,
                              fontWeight: RSS_FEED_EPISODE_DEFAULTS.dateFontWeight,
                            },
                          },
                        ],
                      }}
                    />
                  </ParagraphElement>
                </ContainerElement>
              </ContainerElement>
            ))}
          </div>
        </div>
      </ContainerElement>
    </div>
  );
};
